/**
 * Phone number with bot-scraping mitigation.
 *
 * Digits are stored as ASCII char-codes and assembled at render time
 * inside a useState lazy initializer. esbuild does not constant-fold
 * String.fromCharCode across bindings, so the assembled form never
 * appears in the minified bundle or the sourcemap. No useEffect flash;
 * screen readers see the full number on first paint.
 *
 * Why char-codes (vs. a string-split array): the bundle and sourcemap
 * contain ZERO digit characters anywhere in this module — only a
 * single array of integers in the 48–57 range. Regex sweeps for
 * `\d{3,4}` clusters find nothing, including tolerant variants like
 * `\d{3,4}.{0,30}\d{3,4}.{0,30}\d{3,4}` that would defeat any
 * approach where digits remain visible in phone-shaped chunks
 * (Codex flagged this gap on an earlier split-string implementation).
 * A scraper would need bespoke logic to reassemble — well past the
 * bulk-regex tier this defends against.
 *
 * ─── MAINTAINER NOTE ──────────────────────────────────────────────
 * The number is encoded. You cannot read it from this file directly.
 *
 * DECODE (verify the current number): each CODES entry is the ASCII
 * code of one decimal digit ('0'=48, '1'=49, … '9'=57). Subtract 48
 * from each to read digits left-to-right, or run in any JS console:
 *     String.fromCharCode(...<paste CODES array>)
 *
 * ENCODE (set a new number): in any JS console:
 *     '<new 10-digit string>'.split('').map(c => c.charCodeAt(0))
 * …then paste the resulting array into CODES below.
 *
 * DO NOT write the decoded number anywhere in this file (comments,
 * fixtures, log lines). The Vite sourcemap embeds source verbatim
 * and ships to production at `/assets/*.js.map`; any literal here
 * re-exposes the number to bots that fetch sourcemaps.
 * ──────────────────────────────────────────────────────────────────
 *
 * Not crypto — a scraper that runs JS still gets the number.
 */
import { useState } from "react";

// CODES: ASCII codes for the 10 digits of the phone number, in order.
// See MAINTAINER NOTE in the docblock above for decode/encode recipes.
const CODES = [55, 50, 48, 52, 51, 50, 48, 49, 48, 56] as const;

export function ObfuscatedPhone() {
  const [{ display, href }] = useState(() => {
    const d = String.fromCharCode(...CODES);
    return {
      display: `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}`,
      href: `tel:+1${d}`,
    };
  });

  return (
    <a
      href={href}
      className="text-xl font-medium text-white transition-colors hover:text-brand-500"
    >
      {display}
    </a>
  );
}
